import { useState, useEffect } from "react";
import { Loader2, Save, Trash2 } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { updateBed, deleteBed, updateStructure, deleteStructure, BED_TYPES, SUN } from "@/lib/api";
import { STRUCTURE_TOKENS } from "./structure-types";

/**
 * Edit dialog for any item on the garden map (bed OR structure).
 * Different field sets per kind:
 *  - bed:        name, type, dimensions, sun, soil_notes, position
 *  - structure:  name, type, dimensions, position, notes, capacity_liters (water_*)
 */
export function MapItemEditDialog({ item, kind, open, onOpenChange, onSaved, onDeleted }) {
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!item) return;
        setForm({
            name: item.name || "",
            type: item.type || "",
            length_cm: item.length_cm ?? 200,
            width_cm: item.width_cm ?? 200,
            position_x: item.position_x ?? 0,
            position_y: item.position_y ?? 0,
            sun_exposure: item.sun_exposure || "full sun",
            soil_notes: item.soil_notes || "",
            notes: item.notes || "",
            capacity_liters: item.properties?.capacity_liters ?? "",
            material: item.properties?.material ?? "",
            species: item.properties?.species ?? "",
        });
    }, [item]);

    if (!item) return null;

    const submit = async () => {
        setSaving(true);
        try {
            if (kind === "bed") {
                await updateBed(item.id, {
                    name: form.name,
                    type: form.type || undefined,
                    length_cm: Number(form.length_cm),
                    width_cm: Number(form.width_cm),
                    sun_exposure: form.sun_exposure,
                    soil_notes: form.soil_notes || null,
                    position_x: Number(form.position_x),
                    position_y: Number(form.position_y),
                });
            } else {
                const properties = { ...(item.properties || {}) };
                if (form.capacity_liters !== "") properties.capacity_liters = Number(form.capacity_liters);
                if (form.material !== "") properties.material = form.material;
                if (form.species !== "") properties.species = form.species;
                await updateStructure(item.id, {
                    name: form.name,
                    type: form.type || undefined,
                    length_cm: Number(form.length_cm),
                    width_cm: Number(form.width_cm),
                    position_x: Number(form.position_x),
                    position_y: Number(form.position_y),
                    notes: form.notes || null,
                    properties,
                });
            }
            toast.success(`${form.name || "Item"} updated`);
            onSaved?.();
            onOpenChange(false);
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : (detail?.message || e.message));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm(`Delete ${item.name || "this item"}? This cannot be undone.`)) return;
        try {
            if (kind === "bed") {
                await deleteBed(item.id);
            } else {
                await deleteStructure(item.id);
            }
            toast.success(`${item.name || "Item"} removed`);
            onDeleted?.();
            onOpenChange(false);
        } catch (e) {
            toast.error(e.message);
        }
    };

    const isWater = item.type === "water_tank" || item.type === "water_butt";
    const isPath = item.type === "path";
    const isTree = item.type === "tree";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-[var(--vm-card)] border-[var(--vm-border)] max-w-lg"
                data-testid="map-item-edit-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-serif-display text-2xl capitalize">
                        Edit {kind} · {form.name || item.type}
                    </DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-2 max-h-[65vh] overflow-y-auto pr-2">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="grid gap-2">
                            <Label>Name</Label>
                            <Input data-testid="map-item-name" value={form.name}
                                   onChange={e => setForm({ ...form, name: e.target.value })} />
                        </div>
                        {kind === "bed" ? (
                            <div className="grid gap-2">
                                <Label>Type</Label>
                                <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                                    <SelectTrigger data-testid="map-item-bed-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {BED_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        ) : (
                            <div className="grid gap-2">
                                <Label>Type</Label>
                                <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                                    <SelectTrigger data-testid="map-item-structure-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {Object.keys(STRUCTURE_TOKENS).map(t => (
                                            <SelectItem key={t} value={t}>{STRUCTURE_TOKENS[t].label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="grid gap-2">
                            <Label>Length (cm)</Label>
                            <Input type="number" min="0" data-testid="map-item-length" value={form.length_cm}
                                   onChange={e => setForm({ ...form, length_cm: e.target.value })} />
                        </div>
                        <div className="grid gap-2">
                            <Label>Width (cm)</Label>
                            <Input type="number" min="0" data-testid="map-item-width" value={form.width_cm}
                                   onChange={e => setForm({ ...form, width_cm: e.target.value })} />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="grid gap-2">
                            <Label>X position (cm from left)</Label>
                            <Input type="number" min="0" data-testid="map-item-x" value={form.position_x}
                                   onChange={e => setForm({ ...form, position_x: e.target.value })} />
                        </div>
                        <div className="grid gap-2">
                            <Label>Y position (cm from top)</Label>
                            <Input type="number" min="0" data-testid="map-item-y" value={form.position_y}
                                   onChange={e => setForm({ ...form, position_y: e.target.value })} />
                        </div>
                    </div>

                    {kind === "bed" && (
                        <>
                            <div className="grid gap-2">
                                <Label>Sun exposure</Label>
                                <Select value={form.sun_exposure} onValueChange={v => setForm({ ...form, sun_exposure: v })}>
                                    <SelectTrigger data-testid="map-item-sun"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {SUN.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid gap-2">
                                <Label>Soil notes</Label>
                                <Textarea data-testid="map-item-soil" value={form.soil_notes}
                                          onChange={e => setForm({ ...form, soil_notes: e.target.value })} />
                            </div>
                        </>
                    )}

                    {kind === "structure" && (
                        <>
                            {isWater && (
                                <div className="grid gap-2">
                                    <Label>Capacity (litres)</Label>
                                    <Input type="number" min="0" data-testid="map-item-capacity" value={form.capacity_liters}
                                           onChange={e => setForm({ ...form, capacity_liters: e.target.value })} />
                                </div>
                            )}
                            {isPath && (
                                <div className="grid gap-2">
                                    <Label>Material</Label>
                                    <Input data-testid="map-item-material" placeholder="gravel / wood / stone" value={form.material}
                                           onChange={e => setForm({ ...form, material: e.target.value })} />
                                </div>
                            )}
                            {isTree && (
                                <div className="grid gap-2">
                                    <Label>Species</Label>
                                    <Input data-testid="map-item-species" placeholder="e.g. apple, hazel" value={form.species}
                                           onChange={e => setForm({ ...form, species: e.target.value })} />
                                </div>
                            )}
                            <div className="grid gap-2">
                                <Label>Notes</Label>
                                <Textarea data-testid="map-item-notes" value={form.notes}
                                          onChange={e => setForm({ ...form, notes: e.target.value })} />
                            </div>
                        </>
                    )}
                </div>

                <DialogFooter className="flex-col sm:flex-row gap-2">
                    <Button variant="ghost" onClick={remove} data-testid="map-item-delete"
                            className="text-[var(--vm-secondary)] hover:text-[var(--vm-secondary-hover)]">
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </Button>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} data-testid="map-item-save"
                            className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]">
                        {saving
                            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                            : <><Save className="w-4 h-4 mr-2" /> Save</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
